import React, { useState } from 'react';
import { QuoteData, Pivot } from '../../App';
import { PlusCircleIcon, DownloadIcon, EditIcon, TrashIcon, FileTextIcon, XIcon } from 'lucide-react';
interface QuoteDetailsProps {
  quoteData: QuoteData;
  setQuoteData: (data: QuoteData) => void;
}
export function QuoteDetails({
  quoteData,
  setQuoteData
}: QuoteDetailsProps) {
  const [pivots, setPivots] = useState<Pivot[]>([{
    id: '1',
    name: '215mFC',
    model: 'CP600',
    length: '214.87 m',
    angle: '360°',
    area: '14.5 ha',
    flow: '20.15 l/s',
    quantity: 1
  }, {
    id: '2',
    name: '240mFC',
    model: 'CP600',
    length: '238.63 m',
    angle: '360°',
    area: '17.89 ha',
    flow: '24.85 l/s',
    quantity: 1
  }, {
    id: '3',
    name: '240mFC (1)',
    model: 'CP600',
    length: '238.63 m',
    angle: '360°',
    area: '17.89 ha',
    flow: '24.85 l/s',
    quantity: 1
  }, {
    id: '4',
    name: '336mFC',
    model: 'CP600',
    length: '334.85 m',
    angle: '360°',
    area: '35.22 ha',
    flow: '48.92 l/s',
    quantity: 1
  }]);
  const [selectedPivots, setSelectedPivots] = useState<Set<string>>(new Set());
  const [selectedPivotForDetails, setSelectedPivotForDetails] = useState<Pivot | null>(null);
  const handleAddCP600 = () => {
    const newPivot: Pivot = {
      id: Date.now().toString(),
      name: `New CP600`,
      model: 'CP600',
      length: '0 m',
      angle: '360°',
      area: '0 ha',
      flow: '0 l/s',
      quantity: 1
    };
    setPivots([...pivots, newPivot]);
  };
  const handleAdd8120 = () => {
    const newPivot: Pivot = {
      id: Date.now().toString(),
      name: `New 8120`,
      model: '8120',
      length: '0 m',
      angle: '360°',
      area: '0 ha',
      flow: '0 l/s',
      quantity: 1
    };
    setPivots([...pivots, newPivot]);
  };
  const handleImportFromDesigns = () => {
    alert('Importar desde diseños - Funcionalidad próximamente');
  };
  const handleQuantityChange = (id: string, newQuantity: number) => {
    setPivots(pivots.map(pivot => pivot.id === id ? {
      ...pivot,
      quantity: Math.max(1, newQuantity)
    } : pivot));
  };
  const handleTechnicalSheet = (id: string) => {
    alert(`Ver ficha técnica del pivot ${id} - Funcionalidad próximamente`);
  };
  const handleEditDesign = (id: string) => {
    alert(`Editar diseño del pivot ${id} - Funcionalidad próximamente`);
  };
  const handleDelete = (id: string) => {
    setPivots(pivots.filter(pivot => pivot.id !== id));
    if (selectedPivotForDetails?.id === id) {
      setSelectedPivotForDetails(null);
    }
  };
  const handleRowClick = (pivot: Pivot) => {
    setSelectedPivotForDetails(pivot);
  };
  const handleSelectPivot = (id: string) => {
    const newSelected = new Set(selectedPivots);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedPivots(newSelected);
  };
  const handleSelectAll = () => {
    if (selectedPivots.size === pivots.length) {
      setSelectedPivots(new Set());
    } else {
      setSelectedPivots(new Set(pivots.map(p => p.id)));
    }
  };
  const isAllSelected = pivots.length > 0 && selectedPivots.size === pivots.length;
  const isSomeSelected = selectedPivots.size > 0 && selectedPivots.size < pivots.length;
  return <div className="w-full space-y-6" data-id="element-1007">
      {/* Header Section - Modern Card Design */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden" data-id="element-1008">
        <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-8 relative" data-id="element-1009">
          {/* Pivot icon as background on the right */}
          <div className="absolute right-0 top-0 bottom-0 w-1/3 flex items-center justify-end pr-8" data-id="element-1010">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iw75cXV4WsTP6K2NeDyUPP/8120_western.svg" alt="Pivot Irrigation System" className="w-64 h-64 object-contain opacity-20 cursor-pointer transition-all duration-700 ease-in-out hover:opacity-50 hover:scale-110 hover:rotate-3 hover:drop-shadow-2xl" data-id="element-1011" />
          </div>
          <div className="relative z-10" data-id="element-1012">
            {/* Text and Actions */}
            <div className="space-y-5 max-w-3xl" data-id="element-1013">
              <div data-id="element-1014">
                <div className="flex items-center gap-3 mb-4" data-id="element-1015">
                  <div className="h-px flex-1 bg-gradient-to-r from-blue-200 to-transparent" data-id="element-1016"></div>
                </div>
                <h2 className="text-4xl font-semibold text-gray-900 mb-3 tracking-tight" data-id="element-1017">
                  Select Pivots to Quote
                </h2>
                <p className="text-gray-600 text-base leading-relaxed" data-id="element-1018">
                  Add pivots to your quote by setting up a new configuration,
                  importing from your existing designs, or generating from a
                  template.
                </p>
              </div>
              {/* Primary Action Buttons */}
              <div className="flex flex-wrap gap-3" data-id="element-1019">
                <button onClick={handleAddCP600} className="group relative flex items-center gap-2 px-6 py-3.5 text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-bold shadow-lg hover:shadow-xl hover:scale-105 overflow-hidden" data-id="element-1020">
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" data-id="element-1021"></div>
                  <PlusCircleIcon className="w-5 h-5 relative z-10" data-id="element-1022" />
                  <span className="relative z-10" data-id="element-1023">Add CP600 Pivot</span>
                </button>
                <button onClick={handleAdd8120} className="group relative flex items-center gap-2 px-6 py-3.5 text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all font-bold shadow-lg hover:shadow-xl hover:scale-105 overflow-hidden" data-id="element-1024">
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" data-id="element-1025"></div>
                  <PlusCircleIcon className="w-5 h-5 relative z-10" data-id="element-1026" />
                  <span className="relative z-10" data-id="element-1027">Add 8120 Pivot</span>
                </button>
                <button onClick={handleImportFromDesigns} className="group flex items-center gap-2 px-6 py-3.5 text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:border-blue-500 hover:text-blue-700 hover:shadow-lg transition-all font-bold hover:scale-105" data-id="element-1028">
                  <DownloadIcon className="w-5 h-5 group-hover:animate-bounce" data-id="element-1029" />
                  Import from Designs
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Decorative bottom border */}
        <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" data-id="element-1030"></div>
      </div>
      {/* Pivots Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1031">
        {/* Table Header */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200" data-id="element-1032">
          <div className="flex items-center justify-between" data-id="element-1033">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-1034">
              Selected Pivots ({selectedPivots.size} of {pivots.length})
            </h3>
            {selectedPivots.size > 0 && <button onClick={() => setSelectedPivots(new Set())} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-1035">
                Clear selection
              </button>}
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-1036">
          <table className="w-full" data-id="element-1037">
            <thead data-id="element-1038">
              <tr className="bg-gray-50 border-b border-gray-200" data-id="element-1039">
                <th className="px-6 py-4 text-left w-12" data-id="element-1040">
                  <input type="checkbox" checked={isAllSelected} ref={input => {
                  if (input) {
                    input.indeterminate = isSomeSelected;
                  }
                }} onChange={handleSelectAll} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500" data-id="element-1041" />
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1042">
                  Qty
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1043">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1044">
                  Specifications
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1045">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-1046">
              {pivots.map(pivot => {
              const isSelected = selectedPivots.has(pivot.id);
              return <tr key={pivot.id} onClick={() => handleRowClick(pivot)} className={`transition-colors group cursor-pointer ${isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'}`} data-id="element-1047">
                    {/* Checkbox */}
                    <td className="px-6 py-5" onClick={e => e.stopPropagation()} data-id="element-1048">
                      <input type="checkbox" checked={isSelected} onChange={() => handleSelectPivot(pivot.id)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500" data-id="element-1049" />
                    </td>
                    {/* Quantity */}
                    <td className="px-6 py-5" onClick={e => e.stopPropagation()} data-id="element-1050">
                      <input type="number" min="1" value={pivot.quantity} onChange={e => handleQuantityChange(pivot.id, parseInt(e.target.value) || 1)} className="w-16 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center font-semibold text-gray-900 bg-white" data-id="element-1051" />
                    </td>
                    {/* Name */}
                    <td className="px-6 py-5" data-id="element-1052">
                      <div className="flex items-center gap-3" data-id="element-1053">
                        <div className={`w-2 h-2 rounded-full ${isSelected ? 'bg-blue-600' : 'bg-blue-500'}`} data-id="element-1054"></div>
                        <span className="text-gray-900 font-semibold text-base" data-id="element-1055">
                          {pivot.name}
                        </span>
                      </div>
                    </td>
                    {/* Specifications */}
                    <td className="px-6 py-5" data-id="element-1056">
                      <div className="flex flex-wrap gap-x-4 gap-y-2" data-id="element-1057">
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md ${isSelected ? 'bg-blue-100 text-blue-800' : 'bg-blue-50 text-blue-700'}`} data-id="element-1058">
                          {pivot.model}
                        </span>
                        <span className="text-sm text-gray-600" data-id="element-1059">
                          {pivot.length}
                        </span>
                        <span className="text-sm text-gray-600" data-id="element-1060">
                          {pivot.angle}
                        </span>
                        <span className="text-sm text-gray-600" data-id="element-1061">
                          {pivot.area}
                        </span>
                        <span className="text-sm text-gray-600" data-id="element-1062">
                          {pivot.flow}
                        </span>
                      </div>
                    </td>
                    {/* Actions */}
                    <td className="px-6 py-5" onClick={e => e.stopPropagation()} data-id="element-1063">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1064">
                        <button onClick={() => handleTechnicalSheet(pivot.id)} className="p-2 text-gray-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors" title="Technical Sheet" data-id="element-1065">
                          <FileTextIcon className="w-4 h-4" data-id="element-1066" />
                        </button>
                        <button onClick={() => handleEditDesign(pivot.id)} className="p-2 text-gray-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors" title="Edit Design" data-id="element-1067">
                          <EditIcon className="w-4 h-4" data-id="element-1068" />
                        </button>
                        <button onClick={() => handleDelete(pivot.id)} className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar" data-id="element-1069">
                          <TrashIcon className="w-4 h-4" data-id="element-1070" />
                        </button>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        {/* Empty State */}
        {pivots.length === 0 && <div className="py-20 text-center" data-id="element-1071">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1072">
              <PlusCircleIcon className="w-8 h-8 text-gray-400" data-id="element-1073" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1074">
              No pivots added yet
            </h3>
            <p className="text-gray-500 text-sm" data-id="element-1075">
              Click the buttons above to add your first pivot configuration
            </p>
          </div>}
      </div>
      {/* Summary Card */}
      {pivots.length > 0 && <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl border border-blue-200 p-6" data-id="element-1076">
          <div className="flex items-center justify-between" data-id="element-1077">
            <div data-id="element-1078">
              <h3 className="text-sm font-semibold text-blue-900 uppercase tracking-wide mb-1" data-id="element-1079">
                Quote Summary
              </h3>
              <p className="text-gray-700" data-id="element-1080">
                <span className="font-semibold text-gray-900" data-id="element-1081">
                  {pivots.length}
                </span>{' '}
                pivot configurations •{' '}
                <span className="font-semibold text-gray-900" data-id="element-1082">
                  {pivots.reduce((sum, p) => sum + p.quantity, 0)}
                </span>{' '}
                total units
              </p>
            </div>
          </div>
        </div>}
      {/* Pivot Details Panel */}
      {selectedPivotForDetails && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-1083">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-1084">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 relative" data-id="element-1085">
              <button onClick={() => setSelectedPivotForDetails(null)} className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-lg transition-colors" data-id="element-1086">
                <XIcon className="w-5 h-5" data-id="element-1087" />
              </button>
              <h2 className="text-2xl font-bold text-white mb-2" data-id="element-1088">
                {selectedPivotForDetails.name}
              </h2>
              <span className="inline-flex items-center px-3 py-1 bg-white/20 text-white text-sm font-semibold rounded-full" data-id="element-1089">
                {selectedPivotForDetails.model}
              </span>
            </div>
            {/* Content */}
            <div className="p-6 space-y-6" data-id="element-1090">
              {/* Specifications Grid */}
              <div data-id="element-1091">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1092">
                  Especificaciones Técnicas
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-1093">
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-1094">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-1095">Longitud</p>
                    <p className="text-lg font-semibold text-gray-900" data-id="element-1096">
                      {selectedPivotForDetails.length}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-1097">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-1098">Ángulo</p>
                    <p className="text-lg font-semibold text-gray-900" data-id="element-1099">
                      {selectedPivotForDetails.angle}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-1100">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-1101">Área</p>
                    <p className="text-lg font-semibold text-gray-900" data-id="element-1102">
                      {selectedPivotForDetails.area}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-1103">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-1104">Flujo</p>
                    <p className="text-lg font-semibold text-gray-900" data-id="element-1105">
                      {selectedPivotForDetails.flow}
                    </p>
                  </div>
                </div>
              </div>
              {/* Quantity */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200" data-id="element-1106">
                <p className="text-sm text-blue-700 mb-1" data-id="element-1107">Cantidad</p>
                <p className="text-2xl font-bold text-blue-900" data-id="element-1108">
                  {selectedPivotForDetails.quantity} unidades
                </p>
              </div>
              {/* Actions */}
              <div className="flex gap-3" data-id="element-1109">
                <button onClick={() => {
              handleEditDesign(selectedPivotForDetails.id);
              setSelectedPivotForDetails(null);
            }} className="flex-1 flex items-center justify-center gap-2 px-6 py-3 text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all font-bold shadow-lg" data-id="element-1110">
                  <EditIcon className="w-5 h-5" data-id="element-1111" />
                  Editar Diseño
                </button>
                <button onClick={() => setSelectedPivotForDetails(null)} className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors font-semibold" data-id="element-1112">
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}